import React, { useState } from "react";
import api from "../../lib/api";
import {
  Edit2, Save, X, Mail, Phone, Globe, MapPin, Building2,
  Smartphone, Tag, FileText, Hash, Calendar, User, Plus,
} from "lucide-react";

const FIELD_GROUPS = [
  {
    title: "Kontakt",
    icon: Mail,
    fields: [
      { name: "first_name", label: "Vorname", required: true, icon: User },
      { name: "last_name", label: "Nachname", required: true, icon: User },
      { name: "email", label: "E-Mail", type: "email", icon: Mail, link: v => `mailto:${v}` },
      { name: "phone", label: "Telefon", icon: Phone, link: v => `tel:${v}` },
      { name: "mobile", label: "Mobil", icon: Smartphone, link: v => `tel:${v}` },
      { name: "website", label: "Webseite", icon: Globe, link: v => v.startsWith("http") ? v : `https://${v}`, external: true },
    ],
  },
  {
    title: "Firma & Adresse",
    icon: Building2,
    fields: [
      { name: "company", label: "Firma", icon: Building2 },
      { name: "address", label: "Strasse", icon: MapPin },
      { name: "zip", label: "PLZ", icon: MapPin, half: true },
      { name: "city", label: "Ort", icon: MapPin, half: true },
      { name: "country", label: "Land", icon: MapPin },
    ],
  },
];

function FieldRow({ f, value, editing, onChange }) {
  const Icon = f.icon || FileText;
  return (
    <div className={f.half ? "" : "col-span-2"}>
      <label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground block mb-1.5 flex items-center gap-1">
        <Icon size={10} /> {f.label}{f.required && " *"}
      </label>
      {editing ? (
        <input
          data-testid={`info-field-${f.name}`}
          type={f.type || "text"}
          value={value || ""}
          onChange={e => onChange(f.name, e.target.value)}
          required={f.required}
          className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none"
        />
      ) : value ? (
        f.link ? (
          <a href={f.link(value)} target={f.external ? "_blank" : undefined} rel="noreferrer"
            className="text-sm py-1.5 text-primary hover:underline break-all inline-block">
            {value}
          </a>
        ) : (
          <p className="text-sm py-1.5 break-words">{value}</p>
        )
      ) : (
        <p className="text-sm py-1.5 text-muted-foreground/60 italic">Nicht angegeben</p>
      )}
    </div>
  );
}

export default function InfoTab({ customer, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...customer });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/customers/${customer._id}`, form);
      onUpdate({ ...r.data, stats: customer.stats });
      setEditing(false);
    } finally { setSaving(false); }
  };

  const cancel = () => { setEditing(false); setForm({ ...customer }); };

  const addTag = (e) => {
    e?.preventDefault();
    const t = tagInput.trim();
    if (t && !(form.tags || []).includes(t)) {
      setForm({ ...form, tags: [...(form.tags || []), t] });
      setTagInput("");
    }
  };
  const removeTag = (tag) => setForm({ ...form, tags: form.tags.filter(t => t !== tag) });

  return (
    <div className="animate-in space-y-4">
      {/* Action bar */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold tracking-tight">Kundendaten</h2>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button data-testid="save-info-button" onClick={save} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">
                <Save size={13} /> {saving ? "Speichern..." : "Speichern"}
              </button>
              <button onClick={cancel}
                className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-xs font-semibold hover:bg-secondary/80">
                <X size={13} /> Abbrechen
              </button>
            </>
          ) : (
            <button data-testid="edit-info-button" onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 px-3 py-2 bg-secondary text-xs font-semibold hover:bg-secondary/80">
              <Edit2 size={13} /> Bearbeiten
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left + center: 2 main cards spanning 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {FIELD_GROUPS.map(group => {
            const GroupIcon = group.icon;
            return (
              <div key={group.title} className="bg-card border border-border shadow-soft p-5" data-testid={`info-card-${group.title}`}>
                <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
                  <GroupIcon size={13} className="text-primary" /> {group.title}
                </h3>
                <div className="grid grid-cols-2 gap-x-5 gap-y-1">
                  {group.fields.map(f => (
                    <FieldRow key={f.name} f={f} value={editing ? form[f.name] : customer[f.name]}
                      editing={editing} onChange={change} />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Notizen */}
          <div className="bg-card border border-border shadow-soft p-5" data-testid="info-card-notes">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">
              <FileText size={13} className="text-primary" /> Notizen
            </h3>
            {editing ? (
              <textarea
                data-testid="info-field-notes"
                value={form.notes || ""}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={6}
                placeholder="Allgemeine Notizen, Hinweise, Vereinbarungen..."
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none resize-y"
              />
            ) : customer.notes ? (
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{customer.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground/60 italic">Keine Notizen vorhanden. Klicke auf Bearbeiten, um etwas zu hinterlegen.</p>
            )}
          </div>
        </div>

        {/* Right column: status + tags + meta */}
        <div className="space-y-4">
          {/* Status */}
          <div className="bg-card border border-border shadow-soft p-5">
            <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">Status</h3>
            {editing ? (
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                <option value="active">Aktiv</option>
                <option value="inactive">Inaktiv</option>
              </select>
            ) : (
              <span className={customer.status === "active" ? "status-done text-xs font-bold" : "status-open text-xs font-bold"}>
                {customer.status === "active" ? "Aktiv" : "Inaktiv"}
              </span>
            )}
          </div>

          {/* Tags */}
          <div className="bg-card border border-border shadow-soft p-5" data-testid="info-card-tags">
            <h3 className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">
              <Tag size={12} className="text-primary" /> Tags
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {(editing ? form.tags || [] : customer.tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 text-xs bg-accent text-accent-foreground px-2.5 py-1 rounded-full font-medium">
                  {tag}
                  {editing && (
                    <button onClick={() => removeTag(tag)} className="ml-0.5 text-muted-foreground hover:text-destructive">
                      <X size={10} />
                    </button>
                  )}
                </span>
              ))}
              {!(editing ? form.tags?.length : customer.tags?.length) && !editing && (
                <span className="text-xs text-muted-foreground/60 italic">Keine Tags</span>
              )}
            </div>
            {editing && (
              <form onSubmit={addTag} className="flex gap-1.5">
                <input
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  placeholder="Tag hinzufügen..."
                  className="flex-1 px-2.5 py-1.5 text-xs bg-background border border-input focus:border-primary focus:outline-none"
                />
                <button type="submit" className="px-2.5 py-1.5 bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1">
                  <Plus size={11} /> 
                </button>
              </form>
            )}
          </div>

          {/* Meta */}
          <div className="bg-card border border-border shadow-soft p-5">
            <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">Meta</h3>
            <dl className="space-y-2.5 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground"><Hash size={11} /> Kundennr.</dt>
                <dd className="font-mono font-semibold">{customer.customer_number}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground"><Calendar size={11} /> Erstellt</dt>
                <dd>{customer.created_at ? new Date(customer.created_at).toLocaleDateString("de-CH") : "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground"><Calendar size={11} /> Geändert</dt>
                <dd>{customer.updated_at ? new Date(customer.updated_at).toLocaleDateString("de-CH") : "—"}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
